import { 
  TreePine, 
  Users,
  Briefcase,
  Scale,
  Leaf,
  TrendingUp,
  Banknote,
  GraduationCap,
  PiggyBank
} from 'lucide-react';

export const homeTranslations = {
  sv: {
    hero: {
      title: 'Äger du skog på distans?',
      subtitle: 'Skogskalkyl hjälper dig att få en första överblick över siffror, risker, öppna frågor och nästa steg – utan försäljning, rådgivning eller förmedling.',
      description: 'För dig som vill sortera frågan innan du går vidare.',
      primaryBtn: 'Börja med ett enkelt verktyg',
      secondaryBtn: 'Se kalkylatorerna',
      trustBadge: 'Oberoende orientering baserad på svensk skogsdata',
      accessLogic: 'Testa direkt i webbläsaren – ingen registrering krävs.',
      stats: {
        first: 'Första siffror och uppskattningar',
        second: 'Risker och öppna frågor',
        third: 'Nästa rimliga steg'
      }
    },
    // Section: Target situations - prioritized for distance owners
    situations: {
      title: 'För dig som äger skog på distans',
      items: [
        { text: 'Du äger skog men bor inte i närheten', icon: 'distance' },
        { text: 'Du har ärvt skog men bor inte nära fastigheten', icon: 'heritage' },
        { text: 'Ni är flera delägare och behöver en gemensam bild', icon: 'owners' },
        { text: 'Du vill förstå ett avverkningsförslag innan du går vidare', icon: 'harvest' },
        { text: 'Du överväger att sälja eller behålla skog', icon: 'sell' },
        { text: 'Du funderar på att köpa skog i en annan region', icon: 'buy' },
        { text: 'Du vill förstå en skogsbruksplan eller uppgifter du fått', icon: 'plan' }
      ]
    },
    // Section: Why distance ownership makes things harder
    distanceChallenge: {
      title: 'Varför distansägande gör skogsfrågor svårare',
      text: 'När du inte bor nära din skog saknas ofta vardagsintrycket, de lokala kontakterna och den fackliga rutinen. Då blir det svårare att bedöma angebod, skogsbruksplan, åtgärdsförslag, risker och nästa steg.',
      note: 'Skogskalkyl ersätter ingen fackperson – men hjälper dig att sortera frågan innan du går vidare.'
    },
    // Section: What Skogskalkyl is NOT
    boundaries: {
      title: 'Vad Skogskalkyl inte är',
      items: [
        'inte en skogsbruksplan',
        'inte myndighetsbesked',
        'inte juridisk rådgivning',
        'inte skatterådgivning',
        'inte värderingsintyg',
        'inte förmedling av köpare, säljare eller entreprenörer'
      ],
      positive: 'Skogskalkyl hjälper dig att sortera frågan innan du går vidare till rätt nästa steg.'
    },
    // Section: Beslutsbrev positioning - for distance owners
    beslutsbrev: {
      title: 'Beslutsbrev – en översikt för dig på distans',
      description: 'Ett beslutsbrev ger dig en strukturerad översikt över din situation, viktiga siffror, osäkerheter, risker att kontrollera och frågor att ta vidare med rätt person.',
      positioning: 'För dig som vill ha ett underlag att utgå från – även när du inte är på plats.',
      price: '149 kr',
      exampleTitle: 'Exempel på vad ett beslutsbrev kan innehålla',
      examples: [
        'Kort sammanfattning av din situation',
        'Viktiga siffror och uppskattningar',
        'Osäkerheter att vara medveten om',
        'Risker att kontrollera',
        'Frågor att ställa till rådgivare, bank, mäklare eller Skogsstyrelsen',
        'Nästa steg'
      ],
      cta: 'Se exempel'
    },
    // Section: Trust architecture - strengthened
    trustArchitecture: {
      title: 'Byggt för orientering – inte försäljning',
      items: [
        'Skogskalkyl säljer inte dina uppgifter som leads',
        'Skogskalkyl förmedlar inte virkesköpare, mäklare eller entreprenörer',
        'Skogskalkyl ersätter inte Skogsstyrelsen, skogsbruksplan, jurist, bank, skatterådgivare eller sakkunnig rådgivare',
        'Skogskalkyl hjälper dig att sortera frågan innan du går vidare'
      ]
    },
    tiers: {
      title: 'Tre nivåer – börja gratis',
      subtitle: 'Testa utan inloggning. Spara dina resultat. Fördjupa när det behövs.',
      hint: 'Gratis första genomgång. Betala endast för fördjupning.',
      free: {
        title: 'Gratis genomgång',
        description: 'Få första siffror, se osäkerheter och få en riktning för nästa steg.',
        cta: 'Börja nu'
      },
      registered: {
        title: 'Spara och återkom',
        description: 'Logga in för att spara dina genomgångar och kunna återkomma senare.',
        cta: 'Skapa konto'
      },
      premium: {
        title: 'Beslutsbrev',
        description: 'En strukturerad översikt med siffror, risker, öppna frågor och nästa steg – sparad som PDF.',
        cta: 'Läs mer',
        badge: '149 kr'
      }
    },
    video: {
      label: 'Hur det fungerar',
      title: 'Se ett exempel',
      description: '45 sekunder som visar hur skogen utvecklas över tid – och varför det kan vara bra att ha en bild innan du bestämmer dig.',
      soundOn: 'Ljud på',
      soundOff: 'Ljud av'
    },
    featuredCalcs: {
      title: 'Vanliga frågor att börja med',
      subtitle: 'Välj det som passar din situation',
      recommended: 'Vanlig startpunkt',
      calculators: [
        { id: 'forest-value', name: 'Vad kan skogen vara värd?', desc: 'En första uppskattning av fastighetens värde', icon: Scale, primary: true },
        { id: 'value', name: 'Vad kan virket ge?', desc: 'Uppskatta intäkter vid avverkning', icon: Banknote },
        { id: 'volume', name: 'Hur mycket virke står det?', desc: 'Räkna ut ungefärlig kubik', icon: TreePine },
        { id: 'growth', name: 'Hur utvecklas värdet?', desc: 'Se möjlig tillväxt över tid', icon: TrendingUp },
        { id: 'co2', name: 'Vad lagrar skogen i kol?', desc: 'Förstå klimatnyttan', icon: Leaf }
      ]
    },
    trust: {
      label: 'Datakällor och metodik',
      title: 'Baserat på svensk skogsdata',
      description: 'Kalkylerna bygger på etablerade källor – men resultaten är uppskattningar, inte värderingsintyg.',
      sources: [
        {
          name: 'SLU Riksskogstaxeringen',
          desc: 'Tillväxt- och produktionsdata för svenska skogar'
        },
        {
          name: 'Skogsstyrelsens prisstatistik',
          desc: 'Virkesprisnivåer per region och sortiment'
        },
        {
          name: 'Skogforsk & branschpraxis',
          desc: 'Kostnadsmodeller och avverkningsmetoder'
        }
      ],
      note: 'Resultaten ger dig ett bättre underlag för att förstå din situation – men ersätter inte professionell rådgivning vid större beslut.',
      moreLink: 'Mer om metodik och datakällor'
    },
    faq: {
      title: 'Vanliga frågor',
      items: [
        {
          q: 'Vad är gratis?',
          a: 'Du kan testa alla kalkylatorer och se grundresultat utan kostnad. Ingen registrering krävs.'
        },
        {
          q: 'Hur tillförlitliga är resultaten?',
          a: 'Kalkylerna bygger på data från SLU och Skogsstyrelsen. Resultaten är uppskattningar – inte exakta värden. De ger en utgångspunkt för vidare orientering.'
        },
        {
          q: 'Ersätter Skogskalkyl en värdering?',
          a: 'Nej. Skogskalkyl ger orientering, inte värderingsintyg. Vid försäljning eller juridiska ärenden bör du anlita professionell hjälp.'
        },
        {
          q: 'Säljer ni mina uppgifter?',
          a: 'Nej. Skogskalkyl säljer inte dina uppgifter som leads och förmedlar inte säljare eller entreprenörer.'
        }
      ]
    },
    finalCta: {
      title: 'Få bättre överblick',
      description: 'Testa direkt – ingen inloggning krävs.',
      primaryBtn: 'Börja nu',
      secondaryBtn: 'Skapa konto',
      note: 'Gratis att testa. Konto behövs endast för att spara.'
    },
    seo: {
      text: 'Skogskalkyl hjälper dig som äger skog på distans att få en första överblick över siffror, risker och öppna frågor. Sorterar din fråga innan du går vidare – utan försäljning, rådgivning eller förmedling.'
    },
    loginRequired: 'Logga in krävs',
    goToCalculators: 'Fortsätt till verktyg'
  },
  de: {
    hero: {
      title: 'Von der Zahl zur Entscheidung, auf die Sie bauen können.',
      subtitle: 'Skogskalkyl hilft Ihnen Werte einzuschätzen, Unsicherheiten zu verstehen und den nächsten sinnvollen Schritt zu erkennen – für Entscheidungen mit besserer Grundlage.',
      description: 'Berechnen Sie Holzvolumen, Waldwert und Rendite in Sekunden. Nutzen Sie die gleichen Tools wie professionelle Forstgutachter.',
      primaryBtn: 'Kostenlos starten',
      secondaryBtn: 'Kalkulatoren ansehen',
      trustBadge: 'Basierend auf Daten der SLU Riksskogstaxeringen',
      accessLogic: 'Direkt im Browser testen – keine Registrierung erforderlich.',
      stats: {
        calculators: 'Antworten in Sekunden',
        users: 'Tausende Waldbesitzer',
        free: 'Immer kostenlos'
      }
    },
    tiers: {
      title: 'Von der ersten Schätzung zur klaren Entscheidung',
      subtitle: 'Kostenlos starten. Ergebnisse speichern. Bei Bedarf vertiefen.',
      hint: 'Erstes Ergebnis kostenlos. Zahlen Sie erst, wenn Sie vertiefen möchten.',
      free: {
        title: 'Kostenlos rechnen',
        description: 'Erhalten Sie eine erste Schätzung und verstehen Sie, was das Ergebnis für Wert, Durchforstung und nächste Schritte bedeutet.',
        cta: 'Jetzt rechnen'
      },
      registered: {
        title: 'Speichern und wiederkommen',
        description: 'Öffnen Sie Ihre Berechnungen erneut, vergleichen Sie Alternativen und setzen Sie dort fort, wo Sie aufgehört haben.',
        cta: 'Konto erstellen'
      },
      premium: {
        title: 'Entscheidungsgrundlage aktivieren',
        description: 'Wenn Sie eine bessere Entscheidungsgrundlage benötigen, schalten Sie Unsicherheiten, nächste Schritte und einen Entscheidungsbrief frei.',
        cta: 'Mehr erfahren',
        badge: 'Demnächst'
      }
    },
    video: {
      label: 'Warum es wichtig ist',
      title: 'Sehen Sie, warum die richtige Entscheidung zählt',
      description: '45 Sekunden, die zeigen, wie sich der Wald über 20 Jahre entwickelt – und warum bessere Entscheidungsgrundlagen den Wert beeinflussen.',
      soundOn: 'Ton an',
      soundOff: 'Ton aus'
    },
    whatYouCanDo: {
      title: 'Was können Sie mit Skogskalkyl tun?',
      intro: 'Mit Skogskalkyl können Sie schnelle und einfache Berechnungen für Ihren Wald durchführen.',
      examples: 'Beispiele:',
      items: [
        'Holzvolumen berechnen',
        'Holzwert schätzen',
        'Durchforstung und Ernte planen',
        'Pflanzkosten berechnen',
        'Waldgrundstückswert analysieren',
        'CO₂-Speicherung berechnen'
      ]
    },
    featuredCalcs: {
      title: 'Hier starten',
      subtitle: 'Wählen Sie die Frage, die Sie beantworten möchten',
      recommended: 'Empfohlen für neue Nutzer',
      calculators: [
        { id: 'forest-value', name: 'Was ist mein Wald wert?', desc: 'Gesamtbild des Grundstückswerts', icon: Scale, primary: true },
        { id: 'value', name: 'Was bringt das Holz?', desc: 'Erträge bei Ernte einschätzen', icon: Banknote },
        { id: 'volume', name: 'Wie viel Holz steht dort?', desc: 'Kubikmeter Ihres Bestands berechnen', icon: TreePine },
        { id: 'growth', name: 'Wie entwickelt sich der Wert?', desc: 'Zukünftiges Wachstum und Potenzial', icon: TrendingUp },
        { id: 'co2', name: 'Was speichert der Wald an CO₂?', desc: 'Klimanutzen Ihres Waldes verstehen', icon: Leaf }
      ]
    },
    targetGroups: {
      title: 'Für Waldbesitzer gebaut',
      subtitle: 'Mit Werkzeugen, die auch für Berater und Makler funktionieren.',
      primary: {
        id: 'owners',
        title: 'Waldbesitzer',
        icon: Users,
        description: 'Sie besitzen Wald und möchten verstehen, was er wert ist, was Sie bei einer Maßnahme erwarten können und welcher nächste Schritt sinnvoll ist.',
        examples: [
          'Grundstückswert einschätzen',
          'Durchforstung oder Ernte planen',
          'verschiedene Alternativen vergleichen',
          'sich auf Beratung oder Verkauf vorbereiten'
        ]
      },
      secondary: [
        {
          id: 'advisors',
          title: 'Berater',
          icon: Briefcase,
          description: 'Schnelle Schätzungen zur Unterstützung in Kundengesprächen und Planung.'
        },
        {
          id: 'brokers',
          title: 'Makler und Gutachter',
          icon: Scale,
          description: 'Indikative Analysen vor Bewertung oder Verkaufsaufträgen.'
        }
      ],
      also: 'Wird auch von Investoren, Studenten und Ausbildungsprogrammen in der Forstwirtschaft genutzt.'
    },
    why: {
      title: 'Bessere Entscheidungen im Wald beginnen mit besseren Grundlagen',
      text1: 'Skogskalkyl hilft Ihnen, Wert, Wachstum und Wirtschaftlichkeit zu verstehen – damit Sie mit größerer Sicherheit planen, vergleichen und handeln können.'
    },
    trust: {
      label: 'Datenquellen und Methodik',
      title: 'Basierend auf schwedischen Forstdaten',
      description: 'Unsere Berechnungen nutzen etablierte Quellen und bewährte Modelle – aber die Ergebnisse sind Schätzungen, keine Wertgutachten.',
      sources: [
        {
          name: 'SLU Riksskogstaxeringen',
          desc: 'Wachstums- und Produktionsdaten für schwedische Wälder'
        },
        {
          name: 'Skogsstyrelsens Preisstatistik',
          desc: 'Aktuelle Holzpreisniveaus nach Region und Sortiment'
        },
        {
          name: 'Skogforsk & Branchenpraxis',
          desc: 'Kostenmodelle und Erntemethoden'
        }
      ],
      note: 'Die Ergebnisse geben Ihnen eine bessere Grundlage, um Ihren Wald zu verstehen und mit Beratern zu sprechen – ersetzen aber kein formelles Wertgutachten.',
      moreLink: 'Mehr zu Methodik und Datenquellen'
    },
    faq: {
      title: 'Häufige Fragen',
      items: [
        {
          q: 'Was ist kostenlos?',
          a: 'Sie können alle Rechner testen und Grundresultate sehen – ohne Kosten. Die Berechnung funktioniert sofort, ohne Registrierung.'
        },
        {
          q: 'Wie zuverlässig sind die Ergebnisse?',
          a: 'Unsere Rechner basieren auf Daten von SLU und Skogsstyrelsen. Die Ergebnisse sind modellbasierte Schätzungen – keine exakten Werte, aber ein solider Ausgangspunkt.'
        },
        {
          q: 'Ersetzt Skogskalkyl eine Bewertung?',
          a: 'Nein. Skogskalkyl ist Entscheidungshilfe, kein Wertgutachten. Bei Verkauf oder rechtlichen Angelegenheiten empfehlen wir eine professionelle Bewertung.'
        },
        {
          q: 'Für wen ist Skogskalkyl gedacht?',
          a: 'Primär für Waldbesitzer, die ihren Wald besser verstehen möchten. Auch Berater und Makler nutzen die Werkzeuge für schnelle Einschätzungen.'
        }
      ]
    },
    finalCta: {
      title: 'Starten Sie mit einer Berechnung',
      description: 'Testen Sie direkt – keine Anmeldung erforderlich. Erstellen Sie später ein Konto, wenn Sie Ihre Ergebnisse speichern möchten.',
      primaryBtn: 'Jetzt testen',
      secondaryBtn: 'Konto erstellen',
      note: 'Kostenlos testen. Konto nur zum Speichern erforderlich.'
    },
    register: {
      title: 'Machen Sie bessere Geschäfte mit Ihrem Wald',
      description: 'Konkrete Zahlen statt Vermutungen. Entscheiden Sie auf Basis von Fakten.',
      benefits: [
        'Den Waldwert kennen',
        'Durchforstung präzise planen',
        'Alternativen klar vergleichen',
        'Besser verhandeln'
      ],
      button: 'Kostenlos starten'
    },
    seo: {
      text: 'Skogskalkyl ist eine Plattform für bessere Entscheidungen über Wert, Maßnahmen und Wirtschaftlichkeit Ihres Waldes. Professionelle Berechnungen basierend auf verifizierten Daten von SLU und Skogsstyrelsen.'
    },
    loginRequired: 'Anmeldung erforderlich',
    goToCalculators: 'Zu den Kalkulatoren'
  },
  en: {
    hero: {
      title: 'From numbers to decisions you can stand behind.',
      subtitle: 'Skogskalkyl helps you estimate values, understand uncertainties and see the next reasonable step – so you can act on firmer ground.',
      description: 'Calculate timber volume, forest value and returns in seconds. Use the same tools as professional forest valuers.',
      primaryBtn: 'Get started free',
      secondaryBtn: 'View calculators',
      trustBadge: 'Based on data from SLU National Forest Inventory',
      accessLogic: 'Try directly in your browser – no registration required.',
      stats: {
        calculators: 'Answers in seconds',
        users: 'Thousands of forest owners',
        free: 'Always free'
      }
    },
    tiers: {
      title: 'From first estimate to clearer decisions',
      subtitle: 'Start free. Save your results. Deepen when needed.',
      hint: 'First result free. Pay only when you want to deepen.',
      free: {
        title: 'Calculate free',
        description: 'Get a first estimate and understand what the result means for value, thinning, harvesting and next steps.',
        cta: 'Start calculating'
      },
      registered: {
        title: 'Save and return',
        description: 'Reopen your calculations, compare alternatives and continue where you left off.',
        cta: 'Create account'
      },
      premium: {
        title: 'Activate Decision State',
        description: 'When you need better decision support, unlock uncertainties, next steps and a brief decision letter.',
        cta: 'Learn more',
        badge: 'Coming soon'
      }
    },
    video: {
      label: 'Why it matters',
      title: 'See why the right decision makes a difference',
      description: '45 seconds showing how the forest develops over 20 years – and why better decision support affects value over time.',
      soundOn: 'Sound on',
      soundOff: 'Sound off'
    },
    whatYouCanDo: {
      title: 'What can you do with Skogskalkyl?',
      intro: 'With Skogskalkyl you can make quick and easy calculations for your forest.',
      examples: 'Examples:',
      items: [
        'calculate timber volume',
        'estimate timber value',
        'plan thinning and harvesting',
        'calculate planting costs',
        'analyze forest property value',
        'calculate CO₂ storage'
      ]
    },
    featuredCalcs: {
      title: 'Start here',
      subtitle: 'Choose the question you want answered',
      recommended: 'Recommended for new users',
      calculators: [
        { id: 'forest-value', name: 'What is my forest worth?', desc: 'Get a complete picture of property value', icon: Scale, primary: true },
        { id: 'value', name: 'What can I get for the timber?', desc: 'Estimate income from harvesting', icon: Banknote },
        { id: 'volume', name: 'How much timber is there?', desc: 'Calculate cubic meters in your stand', icon: TreePine },
        { id: 'growth', name: 'How will value develop over time?', desc: 'See future growth and income potential', icon: TrendingUp },
        { id: 'co2', name: 'What does the forest store in carbon?', desc: 'Understand the climate benefit', icon: Leaf }
      ]
    },
    targetGroups: {
      title: 'Built for forest owners',
      subtitle: 'With tools that also work for advisors and brokers.',
      primary: {
        id: 'owners',
        title: 'Forest owners',
        icon: Users,
        description: 'You own forest and want to understand what it\'s worth, what you can expect from an action and what the next reasonable step is.',
        examples: [
          'estimate property value',
          'plan thinning or harvesting',
          'compare different alternatives',
          'prepare for advisory or sale'
        ]
      },
      secondary: [
        {
          id: 'advisors',
          title: 'Advisors',
          icon: Briefcase,
          description: 'Quick estimates to support client meetings and planning.'
        },
        {
          id: 'brokers',
          title: 'Brokers and valuers',
          icon: Scale,
          description: 'Indicative analyses before valuation or sales assignments.'
        }
      ],
      also: 'Also used by investors, students and forestry education programs.'
    },
    why: {
      title: 'Better decisions in the forest start with better information',
      text1: 'Skogskalkyl helps you understand value, growth and economics – so you can plan, compare and act with greater confidence.'
    },
    trust: {
      label: 'Data sources and methodology',
      title: 'Based on Swedish forest data',
      description: 'Our calculations use established sources and proven models – but results are estimates, not formal appraisals.',
      sources: [
        {
          name: 'SLU National Forest Inventory',
          desc: 'Growth and production data for Swedish forests'
        },
        {
          name: 'Swedish Forest Agency pricing',
          desc: 'Current timber price levels by region and assortment'
        },
        {
          name: 'Skogforsk & industry practice',
          desc: 'Cost models and harvesting methods'
        }
      ],
      note: 'Results give you a better basis to understand your forest and discuss with advisors – but do not replace a formal valuation statement.',
      moreLink: 'More about methodology and data sources'
    },
    faq: {
      title: 'Common questions',
      items: [
        {
          q: 'What is free?',
          a: 'You can test all calculators and see basic results at no cost. Calculations work immediately – no registration required.'
        },
        {
          q: 'How reliable are the results?',
          a: 'Our calculators are based on data from SLU and Skogsstyrelsen. Results are model-based estimates – not exact values, but a solid starting point.'
        },
        {
          q: 'Does Skogskalkyl replace a valuation?',
          a: 'No. Skogskalkyl is decision support, not a valuation certificate. For sales or legal matters, we recommend professional valuation.'
        },
        {
          q: 'Who is Skogskalkyl for?',
          a: 'Primarily for forest owners who want to better understand their forest. Advisors and brokers also use the tools for quick estimates.'
        }
      ]
    },
    finalCta: {
      title: 'Start with a calculation',
      description: 'Try directly – no login required. Create an account later if you want to save your results.',
      primaryBtn: 'Try now',
      secondaryBtn: 'Create account',
      note: 'Free to try. Account only required to save.'
    },
    register: {
      title: 'Make better deals with your forest',
      description: 'Get concrete numbers instead of guesses. Make decisions based on facts.',
      benefits: [
        'Know your forest value',
        'Plan thinning with precision',
        'Compare alternatives clearly',
        'Negotiate on better terms'
      ],
      button: 'Get started free'
    },
    seo: {
      text: 'Skogskalkyl is a platform for better decisions about value, actions and economics in your forest. Professional calculations based on verified data from SLU and Skogsstyrelsen.'
    },
    loginRequired: 'Login required',
    goToCalculators: 'Go to calculators'
  },
  se: {
    hero: {
      title: 'Skogskalkyl',
      subtitle: 'Digitála rehkenastit meahcceeaiggádiidda, ráđđeaddjide, investoriidda ja meahccemáklariidda.',
      description: 'Skogskalkyl lea čoakkáldat geavatlaš reaidduin rehkenastit muorravoluma, meahcceekonomiija, šaddama ja meahcceeatnamiid árvvu.',
      primaryBtn: 'Ráhkat konto',
      secondaryBtn: 'Geahča rehkenastimiid'
    },
    video: {
      title: 'Meahcci ovdáneapmi áiggi badjel',
      description: 'Geahča mo meahcci šaddá ja ovdána 20 jagi áigodagas.',
      soundOn: 'Aktivere jiena',
      soundOff: 'Jávkat jiena'
    },
    whatYouCanDo: {
      title: 'Maid sáhtát dahkat Skogskalkyl:iin?',
      intro: 'Skogskalkyl:iin sáhtát dahkat jođánis ja álkes rehkenastimiid du meahccái.',
      examples: 'Ovdamearkkat:',
      items: [
        'rehkenastit muorravoluma',
        'árvvoštallat muorraárvvu',
        'plánet čielggadeami ja ráddjema',
        'rehkenastit gilvengoluid',
        'analyseeret meahcceeatnama árvvu',
        'rehkenastit CO₂-vurkema'
      ]
    },
    featuredCalcs: {
      title: 'Bivnnuhat rehkenastit',
      subtitle: 'Eanemus geavahuvvon reaiddut ruoŧŧelaš meahcceeaiggádiidda',
      calculators: [
        { id: 'volume', name: 'Muorravoluma rehkenastit', desc: 'Rehkenaste kubiihkasisdoalu diametera ja allodaga vuođul', icon: TreePine },
        { id: 'value', name: 'Muorraárvvu rehkenastit', desc: 'Árvvoštala du muorra árvvu dálá haddjiiguin', icon: Banknote },
        { id: 'forest-value', name: 'Meahcceárvvu rehkenastit', desc: 'Rehkenaste ollislaš árvvu du meahcceeatnamis', icon: Scale },
        { id: 'co2', name: 'CO₂ rehkenastit', desc: 'Rehkenaste čáđavurkema ja CO₂-čatnamiid', icon: Leaf },
        { id: 'growth', name: 'Meahccešaddama rehkenastit', desc: 'Árvvoštala boahtteáiggi muorravoluma', icon: TrendingUp }
      ]
    },
    targetGroups: {
      title: 'Geasa lea Skogskalkyl?',
      groups: [
        {
          id: 'owners',
          title: 'Meahcceeaiggádat',
          icon: Users,
          description: 'Meahcceeaiggádat geat háliidit rehkenastit muorraárvvu, meahccešaddama ja meahcceekonomiija.'
        },
        {
          id: 'advisors',
          title: 'Meahccehálddašeaddjit ja ráđđeaddjit',
          icon: Briefcase,
          description: 'Ámmátolbmot geat barget meahcceplánemiin ja ráđđeaddjibálvalusaiguin.'
        },
        {
          id: 'brokers',
          title: 'Meahccemáklarit ja árvvoštallat',
          icon: Scale,
          description: 'Ámmátolbmot geat barget vuovdimiin ja árvvoštallamiin meahcceeatnamiid.'
        },
        {
          id: 'investors',
          title: 'Investorat',
          icon: PiggyBank,
          description: 'Investorat geat árvvoštallet meahcceeatnamiid guhkesáiggi opmodan.'
        },
        {
          id: 'education',
          title: 'Oahpahus ja studeantat',
          icon: GraduationCap,
          description: 'Studeantat ja meahcceoahpahusprogrammat.'
        }
      ]
    },
    why: {
      title: 'Buoret mearrádusat meahccis álget buoret vuođuin',
      text1: 'Skogskalkyl veahkeha du ipmirdit árvvu, šaddama ja ekonomiija – vai sáhtát plánet, buohtastahttit ja doaibmat stuorit oadjebasvuođain.'
    },
    register: {
      title: 'Daga buoret gávppiid du meahcciin',
      description: 'Oaččo konkrehta loguid eaige dušše árvvoštallamiid. Mearrit faktagrundii vuođđuduvvon.',
      benefits: [
        'Geahča maid meahcci lea veara',
        'Pláne čielggadeami presissalažžan',
        'Buohtastahte alternatiivvaid čielgasit',
        'Šiehtadala buoret eavttuin'
      ],
      button: 'Álggat nuvttá'
    },
    seo: {
      text: 'Skogskalkyl lea plattform buoret mearrádusaide árvvu, doaimmaid ja ekonomiija birra du meahccis.'
    },
    loginRequired: 'Sisačáliheapmi gáibiduvvo',
    goToCalculators: 'Rehkenastimiidda'
  }
};
